import {
  createSlice,
  createSelector,
  createAction,
  PayloadAction,
} from '@reduxjs/toolkit';

import { RootState } from 'src/redux/store';
import { DATA_FETCHING_STATUS, DATA_STATE_KEY } from '../../constants';
import { ResponseInterface } from '../../types';
import { EmrMappingsInitialState } from './types';
import {
  OperatorTenantType,
  FacilityType,
  EmrApiProvidersType,
  FacilityMappingStrategyState,
} from 'src/routes/Customers/components/OperatorsTab/modules/types';
import { UUID } from 'src/types/utility';

export const STATE_KEY = 'emrMappings';

export const INITIAL_STATE: EmrMappingsInitialState = {
  operatorTenants: { data: null, status: null },
  emrFacilitiesList: { data: null, status: null },
  facilityTenantDict: { data: null, status: null },
  mapFacilityStatus: null,
  emrApiProviders: { data: null, status: null },
  syncFacilityStatus: null,
  onboardFacilityStatus: null,
  onboardPatientsStatus: null,
};

const slice = createSlice({
  name: STATE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setOperatorTenantsList(
      state,
      action: PayloadAction<ResponseInterface<OperatorTenantType[]>>,
    ) {
      state.operatorTenants = {
        ...action.payload,
      };
    },
    setOperatorTenantsStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.operatorTenants = {
        ...state.operatorTenants,
        status: action.payload,
      };
    },
    setFacilitiesList(
      state,
      action: PayloadAction<ResponseInterface<FacilityType[]>>,
    ) {
      state.emrFacilitiesList = { ...action.payload };
    },
    setFacilitiesStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.emrFacilitiesList = {
        ...state.emrFacilitiesList,
        status: action.payload,
      };
    },
    setMapFacilityStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.mapFacilityStatus = action.payload;
    },
    setEmrApiProviders(state, action: PayloadAction<EmrApiProvidersType[]>) {
      state.emrApiProviders.data = action.payload;
    },
    setEmrApiProvidersStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.emrApiProviders.status = action.payload;
    },
    setSyncFacilityStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.syncFacilityStatus = action.payload;
    },
    setOnboardFacilityStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.onboardFacilityStatus = action.payload;
    },
    setOnboardPatientsStatus(
      state,
      action: PayloadAction<keyof typeof DATA_FETCHING_STATUS>,
    ) {
      state.onboardPatientsStatus = action.payload;
    },
  },
});

export const getState = (state: RootState) =>
  state[DATA_STATE_KEY][STATE_KEY] || INITIAL_STATE;

export const selectors = {
  isLoadingOperatorTenants: createSelector(
    getState,
    state => state.operatorTenants.status === DATA_FETCHING_STATUS.LOADING,
  ),
  isLoadingFacilities: createSelector(
    getState,
    state => state.emrFacilitiesList.status === DATA_FETCHING_STATUS.LOADING,
  ),
  selectOperatorTenants: createSelector(
    getState,
    state => state.operatorTenants.data,
  ),
  selectFacilties: createSelector(
    getState,
    state => state.emrFacilitiesList.data,
  ),
  selectEmrApiProviders: createSelector(
    getState,
    state => state.emrApiProviders.data,
  ),
  selectEmrApiProvidersStatus: createSelector(
    getState,
    state => state.emrApiProviders.status,
  ),
  selectMapFacilitiesStatus: createSelector(
    getState,
    state => state.mapFacilityStatus,
  ),
  selectSyncFacilityStatus: createSelector(
    getState,
    state => state.syncFacilityStatus,
  ),
  selectOnboardFacilityStatus: createSelector(
    getState,
    state => state.onboardFacilityStatus,
  ),
  selectOnboardPatientsStatus: createSelector(
    getState,
    state => state.onboardPatientsStatus,
  ),
};

const extraActions = {
  getEmrApiProviders: createAction(`${STATE_KEY}/getEmrApiProviders`),
  getFacilities: createAction(`${STATE_KEY}/getFacilities`),
  getOperatorTenants: createAction(`${STATE_KEY}/getOperatorTenants`),
  mapFacilities: createAction<{
    data: FacilityMappingStrategyState;
    file: File | null;
  }>(`${STATE_KEY}/mapFacilities`),
  syncFacility: createAction<{ organizationId: UUID; facilityId: string }>(
    `${STATE_KEY}/syncFacility`,
  ),
  parseTenantFile: createAction(`${STATE_KEY}/parseTenantFile`),
  onboardFacility: createAction<{
    emrType: string;
    organizationId: UUID;
    facilityId: string;
    file: File;
  }>(`${STATE_KEY}/onboardFacility`),
  onboardPatients: createAction<{
    emrType: string;
    organizationId: UUID;
    facilityId: string;
    file: File;
  }>(`${STATE_KEY}/onboardPatients`),
};

export const actions = { ...slice.actions, ...extraActions };

const { reducer } = slice;
export default reducer;
